<template>
  <div class="bg-gray-100 w-full h-full rounded-lg overflow-hidden shadow-lg relative">
    <div class="bg-[#075E54] text-white p-4">
      <div class="flex justify-between items-center mb-4">
        <h1 class="text-2xl font-bold">
          {{ $t("WhatsApp") }}
        </h1>
      </div>
      <div class="flex space-x-4">
        <button
          class="flex-1 py-2 font-semibold focus:outline-none transition-colors"
          :class="{
            'border-b-2 border-white': active_tab === 'chats',
            'text-white/70 hover:text-white': active_tab !== 'chats'
          }"
          @click="active_tab = 'chats'">
          {{ $t("Chats") }}
        </button>
        <button
          class="flex-1 py-2 font-semibold focus:outline-none transition-colors"
          :class="{
            'border-b-2 border-white': active_tab === 'contacts',
            'text-white/70 hover:text-white': active_tab !== 'contacts'
          }"
          @click="active_tab = 'contacts'">
          {{ $t("Contacts") }}
        </button>
      </div>
    </div>

    <!-- Chats Tab -->
    <div v-if="active_tab === 'chats'" class="h-[calc(100%-8rem)] flex flex-col">
      <div v-if="chats.length === 0" class="flex flex-col items-center justify-center h-full bg-white p-6">
        <MessageSquarePlus class="w-16 h-16 text-[#128C7E] mb-4" />
        <h2 class="text-2xl font-bold text-gray-800 mb-2">
          {{ $t("Start a new chat") }}
        </h2>
        <p class="text-gray-600 text-center mb-4">
          {{ $t("You don't have any active chats. Start a conversation with one of your contacts!") }}
        </p>
        <button
          class="bg-[#128C7E] text-white px-6 py-2 rounded-md font-semibold hover:bg-[#075E54] transition-colors"
          @click="active_tab = 'contacts'">
          {{ $t("View Contacts") }}
        </button>
      </div>

      <div v-else class="flex flex-col h-full">
        <!-- Chat List -->
        <div v-if="!selected_chat" class="flex-1 overflow-y-auto bg-white">
          <ul class="divide-y divide-gray-200">
            <li v-for="chat in chats" :key="chat.id" class="p-4 hover:bg-gray-100 cursor-pointer" @click="selectChat(chat)">
              <div class="flex items-center space-x-3">
                <div class="flex-shrink-0">
                  <Avatar :name="chat.contact.full_name" :image="chat.contact.avatar" class="h-10 w-10" />
                </div>
                <div class="flex-1 min-w-0 relative">
                  <div class="text-sm font-medium text-gray-900 truncate">
                    {{ chat.name }}
                  </div>
                  <span
                    v-if="chat.unread_messages > 0"
                    class="absolute top-0 right-0 bg-[#128C7E] text-white text-xs px-2 py-1 rounded-full ml-2 mb-1">
                    {{ chat.unread_messages }}
                  </span>
                  <p class="text-sm text-gray-500 truncate mt-1">
                    {{ chat.last_message }}
                  </p>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Chat Interface -->
        <div v-else class="flex flex-col h-full">
          <!-- Chat Header -->
          <div class="bg-[#075E54] text-white p-4 flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <button class="text-white hover:text-gray-200 transition-colors" @click="selected_chat = null">
                <ArrowLeft class="w-6 h-6" :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-180' : '']" />
              </button>
              <div class="relative flex items-center -space-x-2 overflow-hidden">
                <Avatar
                  :name="selected_chat.contact.full_name"
                  :image="selected_chat.contact.avatar"
                  :radius="38"
                  class="h-10 w-10 mr-2 border-2 border-[#075E54]" />
              </div>
              <div>
                <h2 class="text-lg font-semibold">
                  {{ selected_chat.contact.full_name }}
                </h2>
                <p class="text-sm text-gray-300">
                  {{ selected_chat.contact.phone_number }}
                </p>
              </div>
            </div>
          </div>

          <!-- Chat Messages -->
          <div
            ref="messagesContainer"
            class="flex-1 overflow-y-auto p-4 space-y-4 bg-whatsapp-chat bg-repeat relative"
            @scroll="handleScroll">
            <div v-for="(group, groupIndex) in groupedMessages" :key="groupIndex">
              <!-- Date Separator -->
              <div class="text-center text-sm bg-white/80 rounded-lg py-1 px-2 mb-4 inline-block mx-auto">
                {{ formatDate(group.date) }}
              </div>

              <!-- Messages for the given date -->
              <div v-for="message in group.messages" :key="message.id" class="flex flex-col relative">
                <div
                  :class="[
                    'max-w-[80%] rounded-lg p-3 shadow-sm mb-2',
                    message.is_outgoing ? 'ml-auto bg-[#DCF8C6] text-gray-800 rounded-tr-[4px]' : 'bg-white text-gray-800 rounded-tl-[4px]'
                  ]">
                  <p class="text-sm font-medium text-gray-600 mb-1">
                    {{ message.is_outgoing ? "You" : selected_chat.contact.full_name }}
                  </p>

                  <!-- Message content -->
                  <div v-if="message.type === 'image'" class="mb-2">
                    <img
                      :src="message.content"
                      :alt="'Image sent by ' + (message.is_outgoing ? 'You' : selected_chat.contact.full_name)"
                      class="max-w-full h-auto rounded-lg cursor-pointer">
                  </div>
                  <div v-else-if="message.type === 'video'" class="mb-2">
                    <video :src="message.content" controls class="max-w-full h-auto rounded-lg" />
                  </div>
                  <div v-else-if="message.type === 'audio'" class="mb-2">
                    <audio :src="message.content" controls class="w-full" />
                  </div>
                  <div v-else-if="message.type === 'document'" class="mb-2">
                    <a :href="message.content" target="_blank" class="text-blue-600 hover:underline">
                      {{ message.text }}
                    </a>
                  </div>
                  <div v-else-if="message.type === 'contact'" class="mb-2">
                    <p>{{ message.text }}</p>
                    <p class="text-sm text-gray-600">
                      {{ message.content.phone_number }}
                    </p>
                  </div>
                  <div v-else-if="message.type === 'template'">
                    <!-- Template logic -->
                    <div v-if="getTemplateComponent(message.template_name, 'HEADER')" class="mb-2">
                      <img
                        v-if="getTemplateComponent(message.template_name, 'HEADER').format === 'IMAGE'"
                        :src="getTemplateComponent(message.template_name, 'HEADER').example?.header_handle[0]"
                        class="w-full h-32 object-cover rounded-lg mb-2"
                        alt="Header Image">
                      <div v-else class="text-gray-800 font-medium">
                        {{ getTemplateComponent(message.template_name, "HEADER").text }}
                      </div>
                    </div>
                    <div v-if="getTemplateComponent(message.template_name, 'BODY')" class="text-gray-700">
                      {{ formatTemplateText(getTemplateComponent(message.template_name, "BODY")) }}
                    </div>
                    <div v-if="getTemplateComponent(message.template_name, 'FOOTER')" class="text-gray-500 text-sm mt-2">
                      {{ getTemplateComponent(message.template_name, "FOOTER").text }}
                    </div>
                    <div v-if="getTemplateComponent(message.template_name, 'BUTTONS')" class="flex flex-col space-y-2 mt-3 border-t pt-3">
                      <template v-for="(button, index) in getTemplateComponent(message.template_name, 'BUTTONS').buttons" :key="index">
                        <a
                          v-if="button.type === 'URL'"
                          :href="button.url"
                          target="_blank"
                          class="px-4 py-2 bg-[#25D366] text-white rounded-md text-center hover:bg-[#128C7E] transition-colors">
                          {{ button.text }}
                        </a>
                        <button v-else class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors">
                          {{ button.text }}
                        </button>
                      </template>
                    </div>
                  </div>
                  <div v-else-if="message.type === 'button'">
                    <button class="px-4 py-2 bg-[#25D366] text-white rounded-md text-center hover:bg-[#128C7E] transition-colors">
                      {{ message.button.text }}
                    </button>
                  </div>
                  <div v-else-if="message.type === 'interactive'">
                    <div v-if="message.interactive_type === 'list_reply'" class="flex flex-col space-y-2">
                      <div class="text-gray-700">
                        {{ message.text }}
                      </div>
                    </div>
                    <div v-else-if="message.interactive_type === 'button_reply'" class="flex flex-col space-y-2">
                      <div class="text-gray-700">
                        {{ message.text }}
                      </div>
                    </div>
                    <div v-else-if="message.interactive_type === 'nfm_reply'" class="flex flex-col space-y-2">
                      <div class="text-gray-700">
                        {{ message.text }}
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="text-gray-800 whitespace-pre-wrap break-words overflow-hidden w-full"
                    v-html="formatMessageWithLinks(message.description || message.text)" />

                  <!-- Footer: time, reactions, reply -->
                  <div class="text-xs mt-1 flex justify-between items-center space-x-2">
                    <span class="text-gray-500">
                      {{ formatTime(message.timestamp) }}
                    </span>
                    <div class="flex items-center space-x-2">
                      <!-- Reaction Popover -->
                      <Popover class="relative inline-block">
                        <PopoverButton class="hover:text-[#128C7E] transition-colors text-lg outline-none">
                          {{ message.reactions?.length ? message.reactions.map((x) => x.emoji) : "☺" }}
                        </PopoverButton>
                        <transition
                          enter="transition ease-out duration-200"
                          enter-from="opacity-0 translate-x-2"
                          enter-to="opacity-100 translate-x-0"
                          leave="transition ease-in duration-100"
                          leave-from="opacity-100 translate-x-0"
                          leave-to="opacity-0 translate-x-2">
                          <PopoverPanel v-slot="{ close }" class="absolute z-10 right-0 mt-2 outline-none p-2">
                            <EmojiPicker
                              :native="true"
                              :display-recent="true"
                              theme="auto"
                              @select="
                                (emoji) => {
                                  selectEmoji(message, emoji)
                                  close()
                                }
                              " />
                          </PopoverPanel>
                        </transition>
                      </Popover>

                      <button class="hover:text-[#128C7E] transition-colors" @click="setReplyTo(message)">
                        <CornerUpLeft class="w-4 h-4" />
                      </button>
                      <div v-if="message.is_outgoing" class="ml-1">
                        <CheckCheck v-if="message.status === 'delivered'" class="w-4 h-4 text-[#53bdeb]" />
                        <Check v-else-if="message.status === 'sent'" class="w-4 h-4 text-gray-500" />
                        <Clock v-else-if="message.status === 'pending'" class="w-4 h-4 text-gray-400" />
                        <XCircle v-else-if="message.status === 'failed'" class="w-4 h-4 text-red-500" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Input area -->
          <div class="bg-[#F0F0F0] border-t p-4">
            <div v-if="reply_to" class="mb-2 p-2 bg-white rounded-lg flex justify-between items-center">
              <div class="text-sm text-gray-600">
                {{ $t("Replying to") }}: {{ reply_to.text.substring(0, 50) }}...
              </div>
              <button class="text-gray-500 hover:text-gray-700" @click="reply_to = null">
                <X class="w-4 h-4" />
              </button>
            </div>
            <div class="flex items-center space-x-2">
              <input
                v-model="new_message"
                type="text"
                class="flex-1 border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#128C7E]"
                :placeholder="$t('Type a message...')"
                @keydown.enter="sendMessage">
              <button
                class="p-2 rounded-md bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors"
                @click="show_attachment_menu = true">
                <Paperclip class="w-5 h-5" />
              </button>
              <button
                class="p-2 rounded-md bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors"
                @click="show_template_menu = true">
                <SquareMenu class="w-5 h-5" />
              </button>
              <input
                ref="fileInput"
                type="file"
                accept="image/*,video/*,audio/*,application/pdf"
                class="hidden"
                @change="handleFileUpload">
              <button class="p-2 rounded-md bg-[#128C7E] text-white hover:bg-[#075E54] transition-colors" @click="sendMessage">
                <Send class="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contacts Tab -->
    <div v-else class="h-[calc(100%-8rem)] overflow-y-auto bg-white">
      <div class="p-4">
        <div class="w-full flex">
          <input
            v-model="search_query"
            type="text"
            :placeholder="$t('Search contacts...')"
            class="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#128C7E] mb-4">
        </div>
        <ul v-if="filteredContacts.length" class="space-y-2">
          <li
            v-for="contact in filteredContacts"
            :key="contact.id"
            class="flex items-center justify-between p-3 hover:bg-gray-100 border-b border-gray-200 last:border-b-0">
            <div class="flex items-center">
              <Avatar :name="contact.full_name" :image="contact.avatar" :radius="38" class="h-10 w-10 mr-2" />
              <span class="font-medium text-gray-800">{{ contact.full_name }}</span>
            </div>
            <button class="text-[#128C7E] hover:text-[#075E54] font-semibold transition-colors" @click="startChat(contact)">
              {{ $t("Chat") }}
            </button>
          </li>
        </ul>
        <ul v-if="filteredContacts.length === 0" class="p-4 text-center text-gray-500">
          {{
            $t("No contacts found")
          }}
          <button class="text-white hover:text-gray-200 transition-colors" @click="show_add_contact_modal = true">
            <UserPlus class="w-5 h-5" />
          </button>
        </ul>
      </div>
    </div>

    <ChatModals
      v-model:show-template-menu="show_template_menu"
      v-model:show-attachment-menu="show_attachment_menu"
      v-model:show-phone-input-modal="show_phone_input_modal"
      v-model:show-phone-modal="show_phone_modal"
      v-model:selected_contact="selected_contact"
      v-model:selected_phone_metric="selected_phone_metric"
      v-model:selected_contact_phone="selected_contact_phone"
      v-model:selected_chat="selected_chat"
      v-model:new_template="new_template"
      v-model:templates="templates"
      @selectAttachment="selectAttachment"
      @sendTemplate="sendTemplate"
      @updateContact="updateContact"
      @confirmPhoneNumber="confirmPhoneNumber" />
  </div>
</template>

<script>
import {
  // Settings,
  MessageSquarePlus,
  ArrowLeft,
  CornerUpLeft,
  X,
  UserPlus,
  Check,
  CheckCheck,
  Paperclip,
  Clock,
  XCircle,
  Send,
  SquareMenu
} from "lucide-vue-next"
import EmojiPicker from "vue3-emoji-picker"

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue"
import moment from "moment"
import Avatar from "@/components/shared/Avatar.vue"
import integrationService from "@/services/integration.service"
import dataService from "@/services/data.service"
import { useLayoutItemSharedLogic } from "@/components/shared/pages/useLayoutItemSharedLogic.js"
import { utils } from "@/store/helpers"
import ChatModals from "@/components/shared/WhatsAppChat/ChatModals"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import DOMPurify from "dompurify"

export default {
  name: "WhatsAppChat",
  components: {
    EmojiPicker,
    ChatModals,
    PopoverButton,
    PopoverPanel,
    SquareMenu,
    MessageSquarePlus,
    Check,
    CheckCheck,
    Clock,
    XCircle,
    ArrowLeft,
    Send,
    Paperclip,
    X,
    CornerUpLeft,
    UserPlus,
    Avatar,
    Popover
  },
  data() {
    return {
      selected_phone_metric: null,
      active_tab: "chats",
      contacts: [],
      chats: [],
      selected_chat: null,
      new_message: "",
      reply_to: null,
      show_add_contact_modal: false,
      show_attachment_menu: false,
      show_template_menu: false,
      group_name_input: "",
      search_query: "",
      showImageModal: false,
      modal_image: "",
      creating_template: false,
      ai_prompt: "",
      template_prompt: "",
      generating_template: false,
      openai: null,
      new_template: {
        name: "",
        category: "MARKETING",
        language: "en_US",
        components: [],
        is_interactive: false,
        buttons: [],
        header: "",
        header_type: "text",
        header_value: "",
        body: "",
        body_variables: [],
        variable_types: [],
        footer: "",
        button_text: "",
        button_url: "",
        interactive_type: "button",
        list_sections: []
      },
      show_location_picker: false,
      show_contact_picker: false,
      selected_contact: null,
      company_summary: null,
      templates: [],
      available_languages: [
        { code: "en_US", name: this.$t("English (US)") },
        { code: "en_GB", name: this.$t("English (UK)") },
        { code: "fr_FR", name: this.$t("French") },
        { code: "ar_SA", name: this.$t("Arabic (Saudi Arabia)") }
      ],
      parameter_types: [
        { value: "text", label: this.$t("Text") },
        { value: "image", label: this.$t("Image") },
        { value: "currency", label: this.$t("Currency") },
        { value: "date_time", label: this.$t("Date & Time") }
      ],
      loading: false,
      record_error: false,
      component_record: null,
      show_phone_input_modal: false,
      show_phone_modal: false,
      selected_contact_phone: "",
      pagination: {
        count: 0,
        next: null,
        previous: null,
        results: []
      },
      loading_more: false
    }
  },
  computed: {
    integration_id() {
      const integration = this.$store.state.integration.integrations.find((integration) => integration.reference === "whatsapp")
      return integration ? integration.id : null
    },
    current_user() {
      return this.$store.state.auth.user
    },
    groupedMessages() {
      if (!this.selected_chat) {
        return []
      }
      const groups = []
      this.selected_chat.messages.forEach((message) => {
        const messageDate = moment(message.timestamp).format("YYYY-MM-DD")
        let lastGroup = groups[groups.length - 1]

        if (!lastGroup || lastGroup.date !== messageDate) {
          lastGroup = { date: messageDate, messages: [] }
          groups.push(lastGroup)
        }
        lastGroup.messages.push(message)
      })
      return groups
    },
    filteredContacts() {
      return this.contacts.filter((contact) => contact.full_name.toLowerCase().includes(this.search_query.toLowerCase()))
    },
    formatMessageWithLinks() {
      return (text) => {
        if (!text) {
          return ""
        }
        const urlRegex = /https?:\/\/[^\s]+/g
        const htmlContent = text.replace(urlRegex, (url) => {
          const sanitizedUrl = DOMPurify.sanitize(url)
          return `<a href="${sanitizedUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">${sanitizedUrl}</a>`
        })
        return DOMPurify.sanitize(htmlContent, { ADD_ATTR: ["target"] })
      }
    }
  },
  created() {
    const { loading, record_error, component_record } = useLayoutItemSharedLogic(
      this.$props,
      this.postGetComponentRecord,
      this.registerListeners,
      this.unregisterListeners
    )
    this.loading = loading
    this.record_error = record_error
    this.component_record = component_record
  },
  methods: {
    registerListeners() {
      this.$emitter.on(ALL_EMITTERS.NEW_INTERACTION_ADDED, (interaction) => {
        if (interaction.type === "whatsapp" && interaction.record.id === this.component_record.id) {
          if (this.selected_chat && this.selected_chat.thread_id === interaction.thread_id) {
            interaction.contact = interaction.contacts[0]

            this.selected_chat.messages.push(this.parseInteractionToWhatsAppMsg(interaction, this.selected_chat.contact))
          } else {
            this.getThreadsMessages()
          }
        }
      })
      this.$emitter.on(ALL_EMITTERS.INTERACTION_UPDATED, (interaction) => {
        if (interaction.type === "whatsapp" && interaction.record.id === this.component_record.id) {
          if (this.selected_chat && this.selected_chat.thread_id === interaction.thread_id) {
            interaction.contact = interaction.contacts[0]
            //replace the message with the updated one
            this.selected_chat.messages = this.selected_chat.messages.map((message) => {
              if (message.id === interaction.id) {
                return this.parseInteractionToWhatsAppMsg(interaction, this.selected_chat.contact)
              }
              return message
            })
          } else {
            this.getThreadsMessages()
          }
        }
      })
    },
    unregisterListeners() {
      this.$emitter.off(ALL_EMITTERS.NEW_INTERACTION_ADDED)
      this.$emitter.off(ALL_EMITTERS.INTERACTION_UPDATED)
    },
    postGetComponentRecord() {
      this.getThreadsMessages()
      this.getGoogleContacts()
      this.getContacts()
      this.getTemplates()
    },
    getTemplates() {
      integrationService.getWhatsappTemplates(this.integration_id).then((res) => {
        this.templates = res.data.data
      })
    },
    getNextPage() {
      if (this.pagination.next) {
        return dataService.callEndpointByUrl(this.pagination.next).then((res) => {
          res.data.results.forEach((thread) => {
            this.pagination = thread.messages
            thread.messages.results.forEach((interaction) => {
              this.selected_chat.messages.unshift(this.parseInteractionToWhatsAppMsg(interaction, this.selected_chat.contact))
            })
          })
        })
      }
      return Promise.resolve()
    },
    getThreadsMessages(thread_id = null) {
      const filter = {}
      if (thread_id) {
        filter.thread_id = thread_id
      }
      if (this.component_record) {
        filter.record = this.component_record.id
      }

      return dataService.getWhatsAppMessages(filter).then((res) => {
        let chats = []
        res.data.results.forEach((thread) => {
          this.pagination = thread.messages
          let all_messages = thread.messages.results

          const chat = {
            id: thread.thread_id,
            unread_messages: thread.unread_messages,
            thread_id: thread.thread_id,
            name: utils.Record(thread.contact).name,
            contact: utils.Record(thread.contact),
            messages: all_messages.map((m) => this.parseInteractionToWhatsAppMsg(m, thread.contact)).reverse(),
            last_message: this.$t("No messages yet")
          }
          chat.last_message = chat.messages[chat.messages.length - 1]?.text
          if (chat.messages[chat.messages.length - 1]?.reactions.length) {
            let last_reaction =
              chat.messages[chat.messages.length - 1].reactions[chat.messages[chat.messages.length - 1].reactions.length - 1]
            if (last_reaction.is_outgoing) {
              chat.last_message = this.$t("You reacted with") + " " + last_reaction.emoji
            } else {
              chat.last_message = this.$t("Reacted with") + " " + last_reaction.emoji
            }
          }
          chats.push(chat)

          if (thread_id) {
            this.$utils.replaceOrAddItem(this.chats, chat, "thread_id")
          }
        })
        if (!thread_id) {
          this.chats = chats
        }

        if (thread_id && this.selected_chat?.thread_id == thread_id) {
          this.selected_chat.messages.forEach((message) => {
            if (message.status != "read" && message.is_outgoing == false) {
              this.markAsRead(message)
            }
          })
          this.scrollToBottom()
        }
      })
    },
    parseInteractionToWhatsAppMsg(interaction, contact) {
      let whatsapp_msg_type = interaction.meta_data.raw_message.type
      contact = utils.Record(contact)
      let data = {
        id: interaction.id,
        contact: contact,
        message_id: interaction.meta_data.message_id,
        is_outgoing: interaction.meta_data.is_outgoing,
        timestamp: interaction.interaction_date,
        reply_to_interaction_id: interaction.meta_data.reply_to_interaction_id,
        reply_to_wa_message_id: interaction.meta_data.reply_to_wa_message_id,
        last_interaction_id: interaction.meta_data.last_interaction_id,
        reactions: interaction.meta_data.reactions || [],
        reaction: interaction.meta_data.reactions?.find((reaction) => reaction.is_outgoing)?.emoji,
        recipient: contact.phone_number,
        type: whatsapp_msg_type,
        status: interaction.meta_data.status ? interaction.meta_data.status : interaction.meta_data.is_outgoing ? "sent" : "delivered",
        text: ""
      }
      data[whatsapp_msg_type] = interaction.meta_data.raw_message[whatsapp_msg_type]

      if (whatsapp_msg_type === "location") {
        data.text = interaction.meta_data.raw_message.location.name || interaction.meta_data.raw_message.location.address
      } else if (whatsapp_msg_type === "contact") {
        data.text = interaction.meta_data.raw_message.contact.name
        data.content = interaction.meta_data.raw_message.contact
      } else if (whatsapp_msg_type === "document") {
        data.text = interaction.meta_data.raw_message.document?.file_name || interaction.meta_data.raw_message.document?.caption
        data.content = interaction.meta_data.raw_message.document.link
      } else if (whatsapp_msg_type === "image") {
        data.text = interaction.meta_data.raw_message.image.caption
        data.content = interaction.meta_data.raw_message.image.link
      } else if (whatsapp_msg_type === "video") {
        data.text = interaction.meta_data.raw_message.video.caption
        data.content = interaction.meta_data.raw_message.video.link
      } else if (whatsapp_msg_type === "audio") {
        data.text = interaction.meta_data.raw_message.audio.caption
        data.content = interaction.meta_data.raw_message.audio.link
      } else if (whatsapp_msg_type === "template") {
        data.text = interaction.meta_data.raw_message.template.text
        data.template_name = interaction.meta_data.raw_message.template.name
      } else if (whatsapp_msg_type === "text") {
        data.text = interaction.meta_data.raw_message.text.body
      } else if (whatsapp_msg_type === "button") {
        data.text = interaction.meta_data.raw_message.button.text
      } else if (whatsapp_msg_type === "interactive") {
        data.interactive_type = interaction.meta_data.raw_message.interactive.type
        if (data.interactive_type === "list_reply") {
          data.list_reply = interaction.meta_data.raw_message.interactive.list_reply.title?.replace(/^[0-9]+_/g, "").title()
          data.text =
            interaction.meta_data.raw_message.interactive.list_reply.title?.replace(/^[0-9]+_/g, "").title() +
            " - " +
            interaction.meta_data.raw_message.interactive.list_reply.description
        } else if (data.interactive_type === "button_reply") {
          data.button_reply = interaction.meta_data.raw_message.interactive.button_reply.title?.replace(/^[0-9]+_/g, "").title()
          data.text = interaction.meta_data.raw_message.interactive.button_reply.title?.replace(/^[0-9]+_/g, "").title()
        } else if (data.interactive_type === "nfm_reply") {
          data.text = JSON.parse(interaction.meta_data.raw_message.interactive.nfm_reply.response_json)
            ?.screen_0_RadioButtonsGroup_0?.replace(/^[0-9]+_/g, "")
            .title()
          data.nfm_reply = JSON.parse(interaction.meta_data.raw_message.interactive.nfm_reply.response_json)
        }
      }

      return data
    },
    getGoogleContacts() {
      let g_contacts_int_id = this.$store.state.integration.integrations.find(
        (integration) => integration.reference === "google-contacts"
      )?.id
      if (!g_contacts_int_id) {
        return
      }
      integrationService
        .callIntegrationExternalEndpoint(g_contacts_int_id, {
          service_name: "google-contacts",
          action: "get_contacts"
        })
        .then((res) => {
          res.data.forEach((contact) => {
            let values = {}
            values[utils.getOptionMetricReference("contact", "name")] = contact.full_name
            values[utils.getOptionMetricReference("contact", "email")] = contact.email
            values[utils.getOptionMetricReference("contact", "phone_number")] = contact.phone_number
            values[utils.getOptionMetricReference("contact", "first_name")] = contact.first_name
            values[utils.getOptionMetricReference("contact", "last_name")] = contact.last_name
            values["full_name"] = contact.full_name
            contact.values = values
            this.contacts.push(contact)
          })
        })
    },
    getContacts() {
      dataService.getRecords({ parent: this.component_record.id, type: "contact" }).then((res) => {
        res.data.results.forEach((child) => {
          let obj = utils.Record(child)
          const contact = {
            id: child.id,
            full_name: obj.name.title(),
            phone_number: obj.phone_number,
            avatar: obj.avatar,
            ...obj
          }
          this.contacts.push(contact)
        })
      })
    },
    startChat(contact) {
      this.selected_phone_metric = utils.getOptionMetricReference("contact", "phone_number", "reference_name", false)
      this.selected_contact_phone = contact.values[this.selected_phone_metric]
      if (!this.selected_contact_phone || !this.selected_phone_metric) {
        this.selected_contact = contact
        this.show_phone_modal = true
        this.show_phone_input_modal = !!this.selected_phone_metric
        return
      }
      this.initiateChat(contact)
    },
    confirmPhoneNumber() {
      if (!this.selected_contact_phone) {
        return
      }
      utils.Record(this.selected_contact).updateMetricValue(this.selected_phone_metric, this.selected_contact_phone)
      this.selected_contact.phone_number = this.selected_contact_phone
      this.initiateChat(this.selected_contact)
      this.show_phone_modal = false
      this.selected_contact = null
      this.selected_contact_phone = ""
    },
    initiateChat(contact) {
      const existingChat = this.chats.find((chat) => chat.contact.id === contact.id)

      if (existingChat) {
        this.selected_chat = existingChat
      } else {
        const newChat = {
          id: Date.now(),
          name: contact.full_name,
          contact: contact,
          messages: [],
          last_message: this.$t("No messages yet")
        }
        this.chats.push(newChat)
        this.selected_chat = newChat
      }

      this.active_tab = "chats"
    },
    selectChat(chat) {
      this.selected_chat = chat
      this.getThreadsMessages(chat.contact.id).then(() => {
        this.scrollToBottom()
      })
    },
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        setTimeout(() => {
          this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
        }, 100)
      }
    },
    handleScroll(event) {
      const container = event.target
      if (container.scrollTop === 0 && !this.loading_more && this.pagination?.next) {
        this.loading_more = true
        this.getNextPage().then(() => {
          this.loading_more = false
        })
      }
    },
    selectEmoji(message, emoji) {
      // set or unset reaction
      let unicode_emoji = emoji.i
      if (unicode_emoji == message?.reaction?.emoji) {
        unicode_emoji = ""
      }
      this.setReaction(message, unicode_emoji)
    },
    sendMessage() {
      if (this.new_message.trim() && this.selected_chat) {
        const message = {
          id: Date.now(),
          text: this.new_message,
          contact: this.selected_chat.contact,
          is_outgoing: true,
          service_name: "whatsapp",
          action: "send_whatsapp_message",
          timestamp: new Date(),
          reaction: "",
          reply_to_interaction_id: this.reply_to?.id,
          reply_to_wa_message_id: this.reply_to?.message_id,
          last_interaction_id: this.selected_chat.messages[this.selected_chat.messages.length - 1]?.id,
          recipient: this.selected_chat.contact.phone_number,
          type: "text"
        }

        this.selected_chat.messages.push(message)
        this.selected_chat.last_message = this.new_message
        this.new_message = ""
        this.reply_to = null
        integrationService
          .sendWhatsappMessage(this.integration_id, {
            recipient: this.selected_chat.contact.phone_number,
            type: "text",
            last_interaction_id: message.last_interaction_id,
            reply_to_interaction_id: message.reply_to_interaction_id,
            reply_to_wa_message_id: message.reply_to_wa_message_id,
            service_name: "whatsapp",
            action: "send_whatsapp_message",
            text: message.text
          })
          .then((res) => {
            if (res.data && res.data.messages && res.data.messages[0].id) {
              message.id = res.data.interaction_id
              message.message_id = res.data.message_id
              message.interaction_id = res.data.interaction_id
            }
          })
        this.scrollToBottom()
      }
    },
    setReaction(message, reaction) {
      message.reaction = message.reaction == reaction ? "" : reaction
      // replace or push the reaction to the reactions array ""is_outgoing""
      let reaction_obj = {
        emoji: message.reaction,
        is_outgoing: true
      }
      message.reactions = utils.replaceOrAddItem(message.reactions, reaction_obj, "is_outgoing")

      this.reply_to = message
      const recipient = this.selected_chat.contact

      integrationService
        .sendWhatsappMessage(this.integration_id, {
          service_name: "whatsapp",
          action: "send_whatsapp_message",
          reply_to_interaction_id: this.reply_to?.id,
          reply_to_wa_message_id: this.reply_to?.message_id,
          last_interaction_id: this.selected_chat.messages[this.selected_chat.messages.length - 1]?.id || "",
          type: "reaction",
          recipient: recipient.phone_number,
          emoji: reaction
        })
        .then((res) => {
          this.reply_to = null
          if (res.data) {
            message.id = res.data.interaction_id
            message.message_id = res.data.message_id
            message.interaction_id = res.data.interaction_id
          }
        })
    },
    setReplyTo(message) {
      this.reply_to = message
    },
    selectAttachment(type) {
      this.show_attachment_menu = false
      if (["image", "video", "audio", "document"].includes(type)) {
        const fileInput = this.$refs.fileInput
        fileInput.accept = type === "document" ? ".pdf" : `${type}/*`
        fileInput.click()
      }
    },
    handleFileUpload(event) {
      const MAX_FILE_SIZE = {
        image: 5 * 1024 * 1024,
        video: 16 * 1024 * 1024,
        audio: 16 * 1024 * 1024,
        document: 100 * 1024 * 1024
      }
      const SUPPORTED_MIME_TYPES = {
        image: ["image/jpeg", "image/png", "image/jpg"],
        video: ["video/mp4", "video/3gp"],
        audio: ["audio/aac", "audio/mp4", "audio/mpeg", "audio/amr", "audio/ogg"],
        document: [
          "application/pdf",
          "text/plain",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ]
      }
      const getFileCategory = (mimeType) => {
        for (const [category, types] of Object.entries(SUPPORTED_MIME_TYPES)) {
          if (types.includes(mimeType)) {
            return category
          }
        }
        return null
      }

      const file = event.target.files[0]
      if (!file) {
        return
      }

      const fileCategory = getFileCategory(file.type)
      if (!fileCategory) {
        alert(this.$t("Unsupported file type. Please select a valid media file."))
        return
      }
      if (file.size > MAX_FILE_SIZE[fileCategory]) {
        alert(
          this.$t("The file size exceeds the maximum limit for") + ` ${fileCategory} (${MAX_FILE_SIZE[fileCategory] / (1024 * 1024)} MB).`
        )
        return
      }

      if (this.selected_chat) {
        const recipient = this.selected_chat.contact
        const formData = new FormData()
        formData.append("file", file)
        formData.append("recipient", recipient.phone_number)
        formData.append("last_interaction_id", this.selected_chat.messages[this.selected_chat.messages.length - 1]?.id || "")
        formData.append("reply_to_wa_message_id", this.reply_to?.message_id || "")
        formData.append("reply_to_interaction_id", this.reply_to?.id || "")
        formData.append("type", fileCategory)
        formData.append("caption", file.name)
        formData.append("file_name", file.name)
        formData.append("service_name", "whatsapp")
        formData.append("action", "send_whatsapp_message")

        const reader = new FileReader()
        reader.onload = (e) => {
          if (this.selected_chat) {
            const message = {
              id: Date.now(),
              text: `${fileCategory.charAt(0).toUpperCase() + fileCategory.slice(1)}: ${file.name}`,
              timestamp: new Date(),
              reaction: "",
              last_interaction_id: this.selected_chat.messages[this.selected_chat.messages.length - 1]?.id || "",
              reply_to_interaction_id: this.reply_to?.id || "",
              reply_to_wa_message_id: this.reply_to?.message_id || "",
              recipient: recipient.phone_number,
              type: fileCategory,
              content: e.target.result
            }

            this.selected_chat.messages.push(message)
            this.selected_chat.last_message = message.text

            integrationService
              .sendWhatsappMessage(this.integration_id, formData, true)
              .then((res) => {
                if (res.data) {
                  message.id = res.data.interaction_id
                  message.message_id = res.data.message_id
                  message.interaction_id = res.data.interaction_id
                }
              })
              .catch((error) => {
                console.error("Error uploading file:", error)
              })
          }
        }
        reader.readAsDataURL(file)
      }
    },
    updateContact(contact) {
      this.chats.forEach((chat) => {
        if (chat.contact.id === contact.id) {
          chat.contact = contact
        }
      })
    },
    markAsRead(message) {
      if (message.status != "read" && message.is_outgoing == false) {
        integrationService.markWhatsappMessageAsRead(this.integration_id, {
          recipient: this.selected_chat.contact.phone_number,
          type: "text",
          message_id: message.message_id,
          last_interaction_id: message.last_interaction_id,
          reply_to_interaction_id: message.reply_to_interaction_id,
          reply_to_wa_message_id: message.reply_to_wa_message_id,
          service_name: "whatsapp",
          action: "mark_whatsapp_message_as_read",
          text: message.text
        })
      }
    },
    sendTemplate(template) {
      let messageText = ""
      let components = []

      template.components.forEach((component) => {
        if (component.type === "HEADER") {
          messageText += this.formatTemplateText(component) + "\n\n"
          if (component.format === "IMAGE" && component.example?.header_handle?.[0]) {
            components.push({
              type: "header",
              parameters: [{ type: "image", image: { link: component.example.header_handle[0] } }]
            })
          } else if (component.format === "TEXT" && component.example?.header_text?.[0]) {
            components.push({
              type: "header",
              parameters: [{ type: "text", text: component.example.header_text[0] }]
            })
          } else if (component.format === "DOCUMENT" && component.example?.header_handle?.[0]) {
            components.push({
              type: "header",
              parameters: [{ type: "document", document: { link: component.example.header_handle[0] } }]
            })
          } else if (component.format === "VIDEO" && component.example?.header_handle?.[0]) {
            components.push({
              type: "header",
              parameters: [{ type: "video", video: { link: component.example.header_handle[0] } }]
            })
          }
        } else if (component.type === "BODY") {
          let bodyText = component.text
          const paramRegex = /{{(\d+)}}/g
          let match
          const paramValues = component.example?.body_text?.[0] || []
          const bodyParams = []

          while ((match = paramRegex.exec(component.text)) !== null) {
            const paramIndex = parseInt(match[1], 10) - 1
            const paramValue = paramValues[paramIndex] || `[Value ${match[1]}]`
            bodyParams.push({ type: "text", text: paramValue })
            bodyText = bodyText.replace(match[0], paramValue)
          }

          messageText += bodyText + "\n\n"
          if (bodyParams.length > 0) {
            components.push({ type: "body", parameters: bodyParams })
          }
        } else if (component.type === "FOOTER") {
          if (component.text) {
            messageText += component.text + "\n"
          }
        } else if (component.type === "BUTTONS") {
          if (component.buttons) {
            component.buttons.forEach((button, index) => {
              messageText += `[${button.text}]\n`
              if (button.type === "QUICK_REPLY") {
                components.push({
                  type: "button",
                  sub_type: "quick_reply",
                  index: index.toString(),
                  parameters: []
                })
              } else if (button.type === "FLOW") {
                components.push({
                  type: "button",
                  sub_type: "flow",
                  index: index.toString(),
                  parameters: [{ type: "text", text: button.flow_id || "default_flow" }]
                })
              }
            })
          }
        }
      })

      if (components.length === 0) {
        console.error("No components to send for template:", template.name)
        return
      }

      let message = {
        id: Date.now(),
        template_name: template.name,
        timestamp: new Date(),
        reaction: "",
        text: messageText,
        type: "template",
        status: "pending",
        is_outgoing: true,
        contact: this.selected_chat.contact,
        recipient: this.selected_chat.contact,
        template: template
      }

      const userPreferredLanguage = this.selected_chat.contact.language || "en_US"

      const templatePayload = {
        messaging_product: "whatsapp",
        recipient_type: "individual",
        recipient: message.recipient.phone_number,
        to: message.recipient.phone_number,
        type: "template",
        is_outgoing: true,
        last_interaction_id: this.selected_chat.messages[this.selected_chat.messages.length - 1]?.id,
        reply_to_interaction_id: this.reply_to?.id,
        reply_to_wa_message_id: this.reply_to?.meta_data?.raw_message?.message_id,
        template: {
          name: template.name,
          language: { code: userPreferredLanguage },
          components: components
        }
      }

      this.selected_chat.messages.push(message)
      this.selected_chat.last_message = messageText
      this.show_template_menu = false

      integrationService
        .sendWhatsappMessage(this.integration_id, templatePayload)
        .then((res) => {
          if (res.data) {
            message.id = res.data.interaction_id
            message.message_id = res.data.message_id
            message.interaction_id = res.data.interaction_id
          }
          const messageIndex = this.selected_chat.messages.findIndex((m) => m.id === message.id)
          if (messageIndex !== -1) {
            this.selected_chat.messages[messageIndex].status = "sent"
            if (res.messages && res.messages[0]?.id) {
              this.selected_chat.messages[messageIndex].whatsapp_message_id = res.messages[0].id
            }
          }
        })
        .catch((error) => {
          const messageIndex = this.selected_chat.messages.findIndex((m) => m.id === message.id)
          if (messageIndex !== -1) {
            this.selected_chat.messages[messageIndex].status = "failed"
          }
          console.error("Error sending template message:", error)
        })
    },
    getTemplateComponent(templateName, type) {
      const template = this.templates.find((t) => t?.name === templateName)
      if (!template) {
        return null
      }
      return template.components.find((c) => c?.type === type)
    },
    formatTemplateText(component) {
      if (!component || !component.text) {
        return ""
      }
      let text = component.text
      if (component.example?.body_text[0]) {
        const variables = component.example.body_text[0]
        variables.forEach((value, index) => {
          text = text.replace(`{{${index + 1}}}`, value)
        })
      }
      return text
    },
    formatDate(date) {
      return moment(date).format("MMMM D, YYYY")
    },
    formatTime(timestamp) {
      return moment(timestamp).format("HH:mm")
    },
    openImageModal(imageUrl) {
      this.modal_image = imageUrl
      this.showImageModal = true
    },
    closeImageModal() {
      this.showImageModal = false
      this.modal_image = ""
    }
  }
}
</script>

<style scoped>
.bg-whatsapp-chat {
  background-color: #e5ddd5;
  background-image: url("@/assets/whatsapp-texture.jpg");
  background-repeat: repeat;
  background-size: 410px auto;
}
</style>
